import { customerServicePermissionsSchema } from './service-permissions.js';
import { z } from 'zod';
import {
  limitValueSchema,
  planFeaturesSchema,
  planLimitsSchema,
  type PlanFeatures,
  type PlanLimits,
} from './limits.js';
import { planPoliciesSchema, type PlanPolicies } from './service-policies.js';
import type { TransferAvailability } from './transfer.js';

/**
 * The reseller tier (G20, ADR-0033/0034). A reseller owns a slice of clients
 * and plans and operates a scoped panel view; a reseller plan defines the
 * aggregate ceilings and permission caps that govern it.
 */

// ── Value vocabulary ────────────────────────────────────────────────

export const RESELLER_STATUSES = ['active', 'suspended', 'archived'] as const;
export type ResellerStatus = (typeof RESELLER_STATUSES)[number];

/**
 * Plesk overselling toggle. `strict`: the aggregate ceiling is enforced on the
 * sum of allocations (a reseller may not promise more than it holds).
 * `allowed`: allocations may exceed the ceiling; actual usage (G5/G6) may not.
 */
export const RESELLER_OVERSELL_POLICIES = ['strict', 'allowed'] as const;
export type ResellerOversellPolicy = (typeof RESELLER_OVERSELL_POLICIES)[number];

// ── Reseller plans (admin-managed ceiling bundles) ──────────────────

export const createResellerPlanSchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().max(2000).nullish(),
  /** Max clients; `null` = unlimited. */
  maxClients: limitValueSchema.default(null),
  /** Aggregate ceilings keyed by limit descriptor key; absent = no ceiling. */
  resourceCeilings: planLimitsSchema.default({}),
  /** Permission caps keyed by feature key; absent = denied (fail-closed). */
  featureCeilings: planFeaturesSchema.default({}),
  /** Aggregate lifecycle/transfer ceilings; currently monthly transfer applies. */
  policyCeilings: planPoliciesSchema.default({}),
  oversellPolicy: z.enum(RESELLER_OVERSELL_POLICIES).default('strict'),
  isActive: z.boolean().default(true),
});
export type CreateResellerPlanInput = z.infer<typeof createResellerPlanSchema>;

export const updateResellerPlanSchema = createResellerPlanSchema.partial().extend({
  /**
   * Proceed even though the new ceilings drop below the reseller's current
   * aggregate (ISPConfig invariant, ADR-0034). Without it, such an edit is
   * refused with a report; with it, the change lands and the report is
   * audit-logged.
   */
  acknowledgeOverage: z.boolean().optional(),
});
export type UpdateResellerPlanInput = z.infer<typeof updateResellerPlanSchema>;

// ── Resellers ───────────────────────────────────────────────────────

export const createResellerSchema = z.object({
  name: z.string().trim().min(1).max(200),
  company: z.string().trim().max(200).nullish(),
  email: z.string().trim().toLowerCase().email(),
  phone: z.string().trim().max(50).nullish(),
  notes: z.string().max(5000).nullish(),
  /** The reseller plan whose ceilings govern this reseller. Required. */
  resellerPlanId: z.string().uuid(),
});
export type CreateResellerInput = z.infer<typeof createResellerSchema>;

export const updateResellerSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    company: z.string().trim().max(200).nullish(),
    email: z.string().trim().toLowerCase().email().optional(),
    phone: z.string().trim().max(50).nullish(),
    notes: z.string().max(5000).nullish(),
    status: z.enum(RESELLER_STATUSES).optional(),
    resellerPlanId: z.string().uuid().optional(),
    /** Acknowledge a plan reassignment that lowers ceilings below usage. */
    acknowledgeOverage: z.boolean().optional(),
  })
  .strict();
export type UpdateResellerInput = z.infer<typeof updateResellerSchema>;

// ── Reseller-scoped client-portal user management ───────────────────

/**
 * A reseller creating a client-portal login for one of *its own* clients.
 * The role is forced to `client` server-side; `clientId` is validated against
 * the reseller's ownership. Mirrors the staff onboarding flow (temp password
 * + forced change).
 */
export const createResellerPortalUserSchema = z.object({
  name: z.string().trim().min(1).max(120),
  email: z.string().trim().toLowerCase().email(),
  password: z.string().min(12).max(256),
  clientId: z.string().uuid(),
  accountAdmin: z.boolean().default(false),
  servicePermissions: customerServicePermissionsSchema.default([]),
  mustChangePassword: z.boolean().default(true),
});
export type CreateResellerPortalUserInput = z.infer<typeof createResellerPortalUserSchema>;

export const updateResellerPortalUserSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    password: z.string().min(12).max(256).optional(),
    isActive: z.boolean().optional(),
    accountAdmin: z.boolean().optional(),
    servicePermissions: customerServicePermissionsSchema.optional(),
    mustChangePassword: z.boolean().optional(),
  })
  .strict();
export type UpdateResellerPortalUserInput = z.infer<typeof updateResellerPortalUserSchema>;

// ── DTOs ────────────────────────────────────────────────────────────

export interface ResellerPlanDto {
  id: string;
  name: string;
  description: string | null;
  maxClients: number | null;
  resourceCeilings: PlanLimits;
  featureCeilings: PlanFeatures;
  policyCeilings: PlanPolicies;
  oversellPolicy: ResellerOversellPolicy;
  isActive: boolean;
  /** How many resellers are assigned this plan (blocks deletion when > 0). */
  resellerCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ResellerDto {
  id: string;
  name: string;
  company: string | null;
  email: string;
  phone: string | null;
  status: ResellerStatus;
  notes: string | null;
  resellerPlanId: string;
  resellerPlanName: string;
  oversellPolicy: ResellerOversellPolicy;
  /** Owned clients and their subscriptions, rolled up. */
  clientCount: number;
  subscriptionCount: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * The current reseller scope, as `/api/resellers/me` returns it — for a
 * reseller login, or for an admin who is impersonating one. Drives the portal
 * chrome (name, oversell mode) and the impersonation banner.
 */
export interface ResellerScopeDto {
  resellerId: string;
  resellerName: string;
  status: ResellerStatus;
  planId: string;
  planName: string;
  maxClients: number | null;
  oversellPolicy: ResellerOversellPolicy;
  /** True when the caller is an admin impersonating this reseller. */
  impersonating: boolean;
}

/**
 * One line of the ceiling/capacity report (ADR-0034): a ceiling vs the
 * reseller's current aggregate. `mode` says which aggregate the line reflects
 * (allocations under strict oversell, usage under allowed); `advisory` marks
 * byte quotas that are surfaced but not hard-enforced at creation time.
 */
export interface ResellerCeilingLineDto {
  key: string;
  label: string;
  ceiling: number | null;
  aggregate: number;
  mode: 'allocation' | 'usage';
  advisory: boolean;
  overage: number;
  exceeded: boolean;
  /** Present for evidence-backed policy usage such as monthly transfer. */
  availability?: TransferAvailability;
  periodStart?: string;
  periodEnd?: string;
}

export interface ResellerCeilingReportDto {
  resellerId: string;
  oversellPolicy: ResellerOversellPolicy;
  maxClients: number | null;
  clientCount: number;
  maxClientsExceeded: boolean;
  lines: ResellerCeilingLineDto[];
  /** Any hard-enforced line (or max clients) is over its ceiling. */
  hasOverage: boolean;
}
